import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFY_EVENT_KEYS, NOTIFY_EVENT_META, type Announcement, type LinkedAccount, type MergeSummary, type UserAlias, type AnnouncementInput, type DepartmentDef, type LoadingPdiAdminData, type LoadingPdiAliases, type NotifyEvent, type PermCatalogEntry, type Role } from '@technohull/shared';
import { api } from '../lib/api.js';

export interface AdminUser {
  email: string;
  display_name: string;
  role: Role;
  active: number;
  last_seen_at?: string | null;
  departments: string[];
  roles: string[];
  overrides: { permission: string; granted: number }[];
  effective: string[];
  /** their connected Google account (Manage › Google account). */
  google: LinkedAccount | null;
  has_password?: number;
  /** addresses merged into this account (they sign in to it). */
  aliases?: UserAlias[];
}
export interface RoleRef {
  name: string;
  label: string;
}
export interface UsersResponse {
  users: AdminUser[];
  roles: RoleRef[];
  departments: DepartmentDef[];
  catalog: PermCatalogEntry[];
  modules: string[];
  /** "Sign in with Google" is set up on the server. */
  googleSignIn?: boolean;
}
export function useAdminUsers(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<UsersResponse>('/api/admin/users'),
    enabled: opts?.enabled ?? true,
  });
}
export function useSetDepartments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; departments: string[] }) => api.post('/api/admin/users/departments', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useSetUserName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; displayName: string }) => api.post('/api/admin/users/name', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useSetUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; roles: string[] }) => api.post('/api/admin/users/roles', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useSetUserOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; overrides: { permission: string; granted: boolean }[] }) =>
      api.post('/api/admin/users/overrides', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// ── roles administration ────────────────────────────────────────────────────────
export interface RoleRecord {
  name: string;
  label: string;
  description: string | null;
  is_system: number;
  permissions: string[];
}
export interface RolesResponse {
  roles: RoleRecord[];
  catalog: PermCatalogEntry[];
  modules: string[];
}
export function useRoles(opts?: { enabled?: boolean }) {
  return useQuery({ queryKey: ['admin', 'roles'], queryFn: () => api.get<RolesResponse>('/api/admin/roles'), enabled: opts?.enabled ?? true });
}
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; label: string; description?: string }) => api.post('/api/admin/roles', v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'roles'] }); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });
}
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post('/api/admin/roles/delete', { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'roles'] }); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });
}
export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { role: string; permissions: string[] }) => api.post('/api/admin/roles/permissions', v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'roles'] }); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });
}
export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; role: Role }) => api.post('/api/admin/users/role', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useSetActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; active: boolean }) => api.post('/api/admin/users/active', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; displayName?: string; role: Role; password: string }) =>
      api.post('/api/admin/users/create', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useSetPassword() {
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.post('/api/admin/users/password', v),
  });
}
// a user's connected Google account
export function useAdminGoogleLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; google: string }) => api.post<{ ok: true; removed: string | null }>('/api/admin/users/google', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useAdminGooglePrimary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; primary: boolean }) => api.post('/api/admin/users/google/primary', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useAdminGoogleUnlink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post('/api/admin/users/google/disconnect', { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
// two accounts that are the same person: `source` is merged into `target` (and removed)
export function useMergeUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { source: string; target: string }) => api.post<{ ok: true; summary: MergeSummary }>('/api/admin/users/merge', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useRemoveAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alias: string) => api.post('/api/admin/users/alias/remove', { alias }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post('/api/admin/users/delete', { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export interface AuditEntry {
  id: number;
  ts: string;
  actor: string | null;
  action: string;
  resource: string | null;
  detail: string | null;
  ip: string | null;
  status: string | null;
  /** on an entry about a sheet row, when asked for (`describe`): the sheet, the row's boat / work, the columns */
  about?: AuditAbout;
}
export interface AuditAbout {
  sheet: string;
  /** the row's place, outermost first ("BORA", "Antifouling") */
  path: string[];
  columns: string[];
  value?: string;
  /** the row has since been deleted */
  gone?: boolean;
}
/** every audit entry of the past `hours` hours, newest first (`more` when the period held more than the server
 *  sends at once); `describe` also asks what each sheet-row entry was about (slower: it reads those sheets) */
export function useAudit(hours = 24, opts?: { enabled?: boolean; describe?: boolean }) {
  const describe = opts?.describe ?? false;
  return useQuery({
    queryKey: describe ? ['admin', 'audit', 'hours', hours, 'described'] : ['admin', 'audit', 'hours', hours],
    queryFn: () => api.get<{ entries: AuditEntry[]; more?: boolean }>(`/api/admin/audit?hours=${hours}${describe ? '&describe=1' : ''}`),
    enabled: opts?.enabled ?? true,
  });
}

export interface ReportMembers {
  GGO: number;
  ALPHAS: number;
  XPD: number;
}
export interface NotifyEmailConfig {
  eventRoles: Record<string, string[]>; // event → roles emailed
  eventRecipients: Record<string, string[]>; // event → extra addresses added
  eventExcludes: Record<string, string[]>; // event → addresses excluded
  eventTemplates?: Record<string, { subject: string; body: string }>; // event → custom email subject/body (empty = default)
  eventEnabled?: Record<string, boolean>; // event → false disables it entirely (no email, no bell)
}
/** the email-configurable events, in the shared canonical order: key + label + description + the
 *  {placeholders} an admin can insert (all from NOTIFY_EVENT_META in @technohull/shared, which the worker
 *  sends from too). */
export const NOTIFY_EVENTS: { key: NotifyEvent; label: string; desc: string; fields: string[]; audience?: string }[] =
  NOTIFY_EVENT_KEYS.map((key) => ({ key, label: NOTIFY_EVENT_META[key].label, desc: NOTIFY_EVENT_META[key].desc, fields: NOTIFY_EVENT_META[key].fields, audience: NOTIFY_EVENT_META[key].audience }));
export function useAdminConfig() {
  return useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => api.get<{ reportMembers: ReportMembers; reportTargetDate: string; reportPlannedOrders: Record<string, number>; reportPlannedHrs: Record<string, number>; notifyEmail: NotifyEmailConfig; calendarUrl: string; dashboardBoxes: string[] }>('/api/admin/config'),
  });
}
export function useSetConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { reportMembers?: ReportMembers; reportTargetDate?: string; reportPlannedOrders?: Record<string, number>; reportPlannedHrs?: Record<string, number>; notifyEmail?: NotifyEmailConfig; calendarUrl?: string; dashboardBoxes?: string[] }) => api.post('/api/admin/config', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'config'] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['report-log'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** send a test notification email to a chosen recipient (optionally previewing a specific event). */
export function useSendTestNotification() {
  return useMutation({
    // subject/body: the template being edited (unsaved), so it can be tested before saving
    mutationFn: (v: { to: string; event?: string; subject?: string; body?: string }) =>
      api.post<{ ok: boolean; sent: boolean; to: string; event: string | null; reason?: string }>(
        '/api/admin/notifications/test',
        v,
      ),
  });
}

export interface MappingSeason { id: string; overview: string; prod: Record<string, string> }
export interface MappingPdi { key: string; label: string; id: string; colStatus: number; colComments: number }
export interface MappingsSnapshot {
  seasons: MappingSeason[];
  pdiSheets: MappingPdi[];
  generic: { service: string; deck: string; loading: string };
  grp: { id: string; cols: Record<string, number> };
  programming: { id: string; cols: Record<string, number> };
  ovCols: Record<string, number>;
}
export interface MappingsData extends MappingsSnapshot {
  currentSeason: string;
  /** each season's effective PDI sheets (own overrides over the shared baseline), keyed by season id. */
  pdiSheetsBySeason: Record<string, MappingPdi[]>;
  defaults: MappingsSnapshot;
}

export function useAdminMappings() {
  return useQuery({
    queryKey: ['admin', 'mappings'],
    queryFn: () => api.get<MappingsData>('/api/admin/mappings'),
  });
}
function invalidateMappings(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'mappings'] });
  qc.invalidateQueries({ queryKey: ['me'] }); // the season selector is driven by /api/me
}
/** repoint (or create) a single sheet mapping row. */
export function useSaveMappingSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; kind: string; sheetId: string; season?: string | null; family?: string | null; label?: string | null; meta?: Record<string, unknown> | null }) =>
      api.post('/api/admin/mappings/sheet', v),
    onSuccess: () => invalidateMappings(qc),
  });
}
/** reset a sheet to its built-in default by removing its override row. */
export function useDeleteMappingSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string }) => api.post('/api/admin/mappings/sheet/delete', v),
    onSuccess: () => invalidateMappings(qc),
  });
}
/** add a whole season (Overview + the three Production sheets) in one call. */
export function useAddSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; overview: string; prod: Record<string, string>; pdi?: Record<string, string> }) => api.post('/api/admin/mappings/season', v),
    onSuccess: () => invalidateMappings(qc),
  });
}
/** remove every sheet row for a season. */
export function useDeleteSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { season: string }) => api.post('/api/admin/mappings/season/delete', v),
    onSuccess: () => invalidateMappings(qc),
  });
}

/** Mappings › "Loading ↔ PDI items": each loading item's aliases and how many boats it matched in PDI. */
export function useLoadingPdiAdmin() {
  return useQuery({
    queryKey: ['admin', 'loading-pdi'],
    queryFn: () => api.get<LoadingPdiAdminData>('/api/admin/loading-pdi'),
  });
}
/** save the whole alias map; the answer is the recounted admin data. Loading re-matches with it. */
export function useSaveLoadingPdiAliases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { aliases: LoadingPdiAliases }) => api.post<LoadingPdiAdminData>('/api/admin/loading-pdi', v),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'loading-pdi'], data);
      void qc.invalidateQueries({ queryKey: ['loading'] });
    },
  });
}

// ── announcements administration ─────────────────────────────────────────────────
export function useAdminAnnouncements() {
  return useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: () => api.get<{ announcements: Announcement[] }>('/api/admin/announcements'),
  });
}
function invalidateAnnouncements(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
  qc.invalidateQueries({ queryKey: ['announcements', 'feed'] }); // refresh the login popup feed too
}
export function useSaveAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: AnnouncementInput) => api.post<{ ok: true; id: number }>('/api/admin/announcements', v),
    onSuccess: () => invalidateAnnouncements(qc),
  });
}
export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/admin/announcements/delete', { id }),
    onSuccess: () => invalidateAnnouncements(qc),
  });
}
